import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { API_AUTH_RETRY_HEADER } from '../constants/api-auth.constants';
import { environment } from '../environments/environment';

/**
 * Refreshes Trimble OAuth tokens when a SCIT API call returns 401, then retries once.
 */
@Injectable()
export class ApiAuthRefreshInterceptor implements HttpInterceptor {
  private oauth = inject(OAuthService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const apiBaseUrl = environment.MayaTestToolApiBaseUrl;
    if (!apiBaseUrl || !req.url.startsWith(apiBaseUrl)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: unknown) => {
        if (!this.isRetryableUnauthorized(error, req)) {
          return throwError(() => error);
        }

        return from(this.refreshAccessToken()).pipe(
          switchMap(() => {
            const retryReq = req.clone({
              setHeaders: { [API_AUTH_RETRY_HEADER]: '1' },
            });
            return next.handle(retryReq);
          }),
          catchError(() => throwError(() => error))
        );
      })
    );
  }

  private isRetryableUnauthorized(error: unknown, req: HttpRequest<unknown>): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
      return false;
    }
    if (req.headers.has(API_AUTH_RETRY_HEADER)) {
      return false;
    }
    return Boolean(this.oauth.getRefreshToken());
  }

  private refreshAccessToken(): Promise<void> {
    return this.oauth.refreshToken().then(() => undefined);
  }
}
